import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import {
  sendAppointmentScheduledNotification,
  sendAppointmentStatusChangeNotification
} from './zapiService';

class AppointmentService {

  async getAllAppointments(psychologistId, appointmentDate) {
    try {
          const { data: allAppointments, error } = await supabase
            .from('appointments')
            .select('*')                              
            .eq('psychologist_id', psychologistId)
            .eq('appointment_date', appointmentDate)

          if (error) {
            console.error('Erro ao buscar appointments:', error);
          }

          return { data: allAppointments, error: null };

    } catch (error) {
      console.error('Get appointments error:', error);
      return { data: null, error };
    }
  }

  async getExistingAppointments(psychologistId, appointmentDate) {
    try {
          const { data: existingAppointments, error } = await supabase
            .from('appointments')
            .select('*')                              
            .eq('psychologist_id', psychologistId)    
            .eq('appointment_date', appointmentDate)  
             .in('status', ['scheduled', 'confirmed', 'in_progress']);

          if (error) {
            console.error('Erro ao buscar appointments:', error);
          }

          return { data: existingAppointments, error: null };

    } catch (error) {
      console.error('Get appointments error:', error);
      return { data: null, error };
    }
  }

  async getTempAppointments(psychologistId, appointmentDate) {
  try {
        const { data: tempAppointments, error } = await supabase
          .from('temp_public_patients')
          .select('*')
          .eq('psychologist_id', psychologistId)    
          .eq('appointment_date', appointmentDate)

        if (error) {
          console.error('Erro ao buscar temp appointments:', error);
        }

        return { data: tempAppointments, error: null };

    } catch (error) {
      console.error('Get temp appointments error:', error);
      return { data: null, error };
    }
  }


  async getBlockedAppointments(psychologistId, appointmentDate) {
    try {
          const { data: blockedAppointments, error } = await supabase
            .from('appointments')
            .select('*')                              
            .eq('psychologist_id', psychologistId)    
            .eq('appointment_date', appointmentDate)  
            .eq('status', 'blocked');                 

          if (error) {
            console.error('Erro ao buscar blocked appointments:', error);
          }

          return { data: blockedAppointments, error: null };

    } catch (error) {
      console.error('Get blocked appointments error:', error);
      return { data: null, error };
    }
  }

  async createAppointment(appointmentData) {
    try {
      const { data, error } = await supabase?.from('appointments')?.insert([appointmentData])?.select(`
          *,
          patient:patients(*)
        `)?.single();

      if (error) throw error;

      // 📲 Send WhatsApp notification to patient for new appointment
      if (data?.patient?.whatsapp_number || data?.patient?.phone) {
        try {
          //console.log('📤 Enviando notificação de agendamento para:', data?.patient?.full_name);
          await sendAppointmentScheduledNotification(data, data?.patient);
        } catch (notificationError) {
          console.error('⚠️ Erro ao enviar notificação WhatsApp (agendamento):', notificationError);
          // Don't throw error - appointment was created successfully
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Create appointment error:', error);
      return { data: null, error };
    }
  }

  async updateAppointment(id, updates) {
    try {
      // Get the current appointment to detect changes
      const { data: currentAppointment } = await supabase?.from('appointments')?.select(`
          *,
          patient:patients(*)
        `)?.eq('id', id)?.single();

      const { data, error } = await supabase?.from('appointments')?.update(updates)?.eq('id', id)?.select(`
          *,
          patient:patients(*)
        `)?.single();

      if (error) throw error;

      // 📲 Send WhatsApp notification if appointment was modified
      if (data?.patient?.whatsapp_number || data?.patient?.phone) {
        try {
          // Check if status changed
          if (currentAppointment && updates?.status && currentAppointment?.status !== updates?.status) {
            //console.log('📤 Enviando notificação de mudança de status para:', data?.patient?.full_name);
            await sendAppointmentStatusChangeNotification(
              data,
              data?.patient,
              currentAppointment?.status,
              updates?.status
            );
          }
          // Check if date/time changed (modification)
          else if (
            currentAppointment &&
            (updates?.appointment_date || updates?.start_time || updates?.end_time) &&
            (currentAppointment?.appointment_date !== data?.appointment_date ||
             currentAppointment?.start_time !== data?.start_time ||
             currentAppointment?.end_time !== data?.end_time)
          ) {
            //console.log('📤 Enviando notificação de modificação de horário para:', data?.patient?.full_name);
            // Send as status change with "modified" context
            await sendAppointmentStatusChangeNotification(
              data,
              data?.patient,
              'scheduled',
              'scheduled'
            );
          }
        } catch (notificationError) {
          console.error('⚠️ Erro ao enviar notificação WhatsApp (atualização):', notificationError);
          // Don't throw error - appointment was updated successfully
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Update appointment error:', error);
      return { data: null, error };
    }
  }

  async deleteAppointment(id) {
    try {
      const { data, error } = await supabase?.from('appointments')
      .delete()
      .eq('id', id)
      .select();

      if (error) throw error;

      if (data.length === 0) {
        // Registro não existe.
        // Tentar apagar da temp_public_patients.
        const { data, error } = await supabase?.from('temp_public_patients')
        .delete()
        .eq('id', id)

        if (error) throw error;
      }

      return { error: null };
      
    } catch (error) {
      console.error('Delete appointment error:', error);
      return { error };
    }
  }

  async getAppointmentStats(psychologistId, startDate, endDate) {
    try {
      const { data, error } = await supabase?.from('appointments')?.select('status, appointment_type')?.eq('psychologist_id', psychologistId)?.gte('appointment_date', startDate)?.lte('appointment_date', endDate);

      if (error) throw error;

      const stats = {
        total: data?.length,
        scheduled: data?.filter(a => a?.status === 'scheduled')?.length,
        confirmed: data?.filter(a => a?.status === 'confirmed')?.length,
        completed: data?.filter(a => a?.status === 'completed')?.length,
        cancelled: data?.filter(a => a?.status === 'cancelled')?.length,
        no_show: data?.filter(a => a?.status === 'no_show')?.length
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Get appointment stats error:', error);
      return { data: null, error };
    }
  }

  async checkConflicts(psychologistId, appointmentDate, startTime, endTime, excludeId = null) {
    try {

      const formattedStartTime = startTime + ':00';
      const formattedEndTime = endTime + ':00';

      let query = supabase
      ?.from('appointments')
      ?.select('id, start_time, end_time')
      ?.eq('psychologist_id', psychologistId)
      ?.eq('appointment_date', appointmentDate)
      ?.neq('status', 'cancelled')
      ?.lt('start_time', formattedEndTime)
      ?.gt('end_time', formattedStartTime);

      if (excludeId) {
        query = query?.neq('id', excludeId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { hasConflict: data?.length > 0, conflicts: data, error: null };
    } catch (error) {
      console.error('Check conflicts error:', error);
      return { hasConflict: false, conflicts: null, error };
    }
  }

  async getPublicPsychologistInfo(psychologistId) {
    try {
      // First, try to find by psychologist ID
      let { data, error } = await supabase
        ?.from('psychologists')
        ?.select('id, full_name, specialization, consultation_duration, consultation_price, currency, clinic_name, clinic_logo_url, profile_photo_url, public_page_enabled, work_lunch_interval, work_begin_time, work_end_time')
        ?.eq('id', psychologistId)
        ?.maybeSingle();

      // If not found by psychologist ID, try to find by user_id (for backwards compatibility)
      if (!data && !error) {
        const result = await supabase
          ?.from('psychologists')
          ?.select('id, full_name, specialization, consultation_duration, consultation_price, currency, clinic_name, clinic_logo_url, profile_photo_url, public_page_enabled, work_lunch_interval, work_begin_time, work_end_time')
          ?.eq('user_id', psychologistId)
          ?.maybeSingle();
        
        data = result?.data;
        error = result?.error;
      }

      if (error) throw error;
      
      // If still no psychologist found after both searches, return proper error
      if (!data) {
        return { 
          data: null, 
          error: { 
            message: 'Psicólogo não encontrado. Verifique se o link está correto.' 
          } 
        };
      }
      
      // If psychologist found but public page is disabled, show a friendly message
      if (data && !data?.public_page_enabled) {
        return { 
          data: null, 
          error: { 
            message: 'Esta página de agendamento está temporariamente desabilitada. Entre em contato diretamente com o profissional.' 
          }
        };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Get public psychologist info error:', error);
      return { data: null, error };
    }
  }
}

/**
 * Create a new appointment
 */
export const createAppointment = async (appointmentData) => {
  try {
    const { data, error } = await supabase?.from('appointments')?.insert([appointmentData])?.select(`
        *,
        patient:patients(*)
      `)?.single();

    if (error) throw error;

    // Send WhatsApp notification to patient
    if (data?.patient?.whatsapp_number || data?.patient?.phone) {
      try {
        await sendAppointmentScheduledNotification(data, data?.patient);
      } catch (notificationError) {
        console.error('Erro ao enviar notificação WhatsApp:', notificationError);
        // Don't throw error - appointment was created successfully
      }
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error creating appointment:', error);
    return { data: null, error };
  }
};

/**
 * Update an appointment
 */
export const updateAppointment = async (id, updates) => {
  try {
    // Get the current appointment to compare status
    const { data: currentAppointment } = await supabase?.from('appointments')?.select('status')?.eq('id', id)?.single();

    const { data, error } = await supabase?.from('appointments')?.update(updates)?.eq('id', id)?.select(`
        *,
        patient:patients(*)
      `)?.single();

    if (error) throw error;

    // Send WhatsApp notification if status changed
    if (
      currentAppointment &&
      updates?.status &&
      currentAppointment?.status !== updates?.status &&
      (data?.patient?.whatsapp_number || data?.patient?.phone)
    ) {
      try {
        await sendAppointmentStatusChangeNotification(
          data,
          data?.patient,
          currentAppointment?.status,
          updates?.status
        );
      } catch (notificationError) {
        console.error('Erro ao enviar notificação WhatsApp:', notificationError);
        // Don't throw error - appointment was updated successfully
      }
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating appointment:', error);
    return { data: null, error };
  }
};

/**
 * Create a public booking (for non-authenticated users)
 */
export const createPublicBooking = async (bookingData) => {
  try {
    const { fullName, whatsappNumber, appointmentDate, startTime, psychologistId } = bookingData;
    
    // Get psychologist info to calculate end time
    const { data: psychologist, error: psychologistError } = await supabase?.from('psychologists')?.select('consultation_duration')?.eq('id', psychologistId)?.single();

    if (psychologistError) throw psychologistError;

    const duration = psychologist?.consultation_duration || 50;
    
    // ✅ Use the public patient service to save to temp_public_patients table
    const { publicPatientService } = await import('./publicPatientService');
    
    return await publicPatientService?.createPublicPatientBooking({
      psychologistId,
      fullName,
      whatsappNumber,
      appointmentDate,
      startTime,
      duration
    });
  } catch (error) {
    console.error('Error creating public booking:', error);
    return { data: null, error };
  }
};

export const appointmentService = new AppointmentService();
